// Baza danych materiałów i parametrów gięcia dla PlexiSystem
// Zawiera dane dla maszyn Shannon HRK 1200/2000 oraz GT 2000

export interface BendingMaterial {
  id: string;
  name: string;
  type: 'PMMA_XT' | 'PMMA_GS' | 'PC' | 'PETG' | 'PVC_FOAM';
  densityKgM3: number;
  thermalConductivity: number; // W/(m·K)
  specificHeat: number; // J/(kg·K)
  glassTempMin: number; // °C
  glassTempMax: number; // °C
  maxBendingTemp: number; // °C
  coolingRate: number; // °C/min
  springBackFactor: number; // współczynnik sprężynowania 0-1
  moistureSensitive: boolean;
  bendingCoefficient: number; // mnożnik czasu na mm grubości
  warningTemp: number; // temperatura powyżej której pojawiają się defekty
}

export interface BendingMachine {
  id: string;
  name: string;
  model: string;
  maxLength: number; // mm
  tempRangeMin: number; // °C
  tempRangeMax: number; // °C
  heatingZones: number;
  hasUpperHeater: boolean;
  hasLowerHeater: boolean;
  defaultHeatingWidth: number; // mm - szerokość strefy grzania
}

export interface BendingParameters {
  materialId: string;
  thickness: number; // mm
  bendLength: number; // mm
  bendAngle: number; // stopnie
  bendRadius: number; // mm
  ambientTemp: number; // °C
  humidity: number; // %
  machineId: string;
}

export interface BendingResult {
  upperHeaterTemp: number; // °C
  lowerHeaterTemp: number; // °C
  heatingTime: number; // sekundy
  heatingZoneWidth: number; // mm
  coolingTime: number; // sekundy
  springBackCompensation: number; // stopnie
  estimatedCycleTime: number; // sekundy
  warnings: string[];
  recommendations: string[];
}

export interface BendingDefect {
  name: string;
  cause: string;
  solution: string;
  preventionTemp?: { min?: number; max?: number };
  preventionTime?: { min?: number; max?: number };
}

// Baza materiałów do gięcia
export const BENDING_MATERIALS: BendingMaterial[] = [
  {
    id: 'pmma_xt_3',
    name: 'Plexi XT 3mm',
    type: 'PMMA_XT',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 100,
    glassTempMax: 115,
    maxBendingTemp: 160,
    coolingRate: 2,
    springBackFactor: 0.15,
    moistureSensitive: true,
    bendingCoefficient: 15, // 15 sekund na mm
    warningTemp: 170
  },
  {
    id: 'pmma_xt_5',
    name: 'Plexi XT 5mm',
    type: 'PMMA_XT',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 100,
    glassTempMax: 115,
    maxBendingTemp: 160,
    coolingRate: 2,
    springBackFactor: 0.18,
    moistureSensitive: true,
    bendingCoefficient: 12, // mniej czasu na mm dla grubszych
    warningTemp: 170
  },
  {
    id: 'pmma_xt_8',
    name: 'Plexi XT 8mm',
    type: 'PMMA_XT',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 105,
    glassTempMax: 120,
    maxBendingTemp: 165,
    coolingRate: 1.5,
    springBackFactor: 0.20,
    moistureSensitive: true,
    bendingCoefficient: 10,
    warningTemp: 175
  },
  {
    id: 'pmma_xt_10',
    name: 'Plexi XT 10mm',
    type: 'PMMA_XT',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 105,
    glassTempMax: 120,
    maxBendingTemp: 165,
    coolingRate: 1.2,
    springBackFactor: 0.22,
    moistureSensitive: true,
    bendingCoefficient: 9,
    warningTemp: 175
  },
  {
    id: 'pmma_gs_3',
    name: 'Plexi GS 3mm',
    type: 'PMMA_GS',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 110,
    glassTempMax: 125,
    maxBendingTemp: 170,
    coolingRate: 2.5,
    springBackFactor: 0.12,
    moistureSensitive: true,
    bendingCoefficient: 18, // GS potrzebuje więcej czasu
    warningTemp: 180
  },
  {
    id: 'pmma_gs_5',
    name: 'Plexi GS 5mm',
    type: 'PMMA_GS',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 110,
    glassTempMax: 125,
    maxBendingTemp: 170,
    coolingRate: 2,
    springBackFactor: 0.14,
    moistureSensitive: true,
    bendingCoefficient: 15,
    warningTemp: 180
  },
  {
    id: 'pmma_gs_8',
    name: 'Plexi GS 8mm',
    type: 'PMMA_GS',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 115,
    glassTempMax: 130,
    maxBendingTemp: 175,
    coolingRate: 1.5,
    springBackFactor: 0.16,
    moistureSensitive: true,
    bendingCoefficient: 12,
    warningTemp: 185
  },
  {
    id: 'pmma_gs_10',
    name: 'Plexi GS 10mm',
    type: 'PMMA_GS',
    densityKgM3: 1190,
    thermalConductivity: 0.19,
    specificHeat: 1465,
    glassTempMin: 115,
    glassTempMax: 130,
    maxBendingTemp: 175,
    coolingRate: 1.2,
    springBackFactor: 0.18,
    moistureSensitive: true,
    bendingCoefficient: 11,
    warningTemp: 185
  },
  {
    id: 'pc_3',
    name: 'Poliwęglan 3mm',
    type: 'PC',
    densityKgM3: 1200,
    thermalConductivity: 0.21,
    specificHeat: 1200,
    glassTempMin: 145,
    glassTempMax: 155,
    maxBendingTemp: 180,
    coolingRate: 3,
    springBackFactor: 0.25,
    moistureSensitive: false,
    bendingCoefficient: 20,
    warningTemp: 190
  },
  {
    id: 'pc_5',
    name: 'Poliwęglan 5mm',
    type: 'PC',
    densityKgM3: 1200,
    thermalConductivity: 0.21,
    specificHeat: 1200,
    glassTempMin: 145,
    glassTempMax: 155,
    maxBendingTemp: 180,
    coolingRate: 2.5,
    springBackFactor: 0.28,
    moistureSensitive: false,
    bendingCoefficient: 18,
    warningTemp: 190
  },
  {
    id: 'pc_8',
    name: 'Poliwęglan 8mm',
    type: 'PC',
    densityKgM3: 1200,
    thermalConductivity: 0.21,
    specificHeat: 1200,
    glassTempMin: 150,
    glassTempMax: 160,
    maxBendingTemp: 185,
    coolingRate: 2,
    springBackFactor: 0.30,
    moistureSensitive: false,
    bendingCoefficient: 15,
    warningTemp: 195
  },
  {
    id: 'petg_3',
    name: 'PETG 3mm',
    type: 'PETG',
    densityKgM3: 1270,
    thermalConductivity: 0.29,
    specificHeat: 1100,
    glassTempMin: 80,
    glassTempMax: 90,
    maxBendingTemp: 120,
    coolingRate: 4,
    springBackFactor: 0.10,
    moistureSensitive: false,
    bendingCoefficient: 10,
    warningTemp: 130
  },
  {
    id: 'petg_5',
    name: 'PETG 5mm',
    type: 'PETG',
    densityKgM3: 1270,
    thermalConductivity: 0.29,
    specificHeat: 1100,
    glassTempMin: 80,
    glassTempMax: 90,
    maxBendingTemp: 120,
    coolingRate: 3,
    springBackFactor: 0.12,
    moistureSensitive: false,
    bendingCoefficient: 8,
    warningTemp: 130
  },
  {
    id: 'petg_8',
    name: 'PETG 8mm',
    type: 'PETG',
    densityKgM3: 1270,
    thermalConductivity: 0.29,
    specificHeat: 1100,
    glassTempMin: 85,
    glassTempMax: 95,
    maxBendingTemp: 125,
    coolingRate: 2.5,
    springBackFactor: 0.14,
    moistureSensitive: false,
    bendingCoefficient: 7,
    warningTemp: 135
  },
  {
    id: 'pvc_foam_3',
    name: 'PVC spienione 3mm',
    type: 'PVC_FOAM',
    densityKgM3: 550,
    thermalConductivity: 0.08,
    specificHeat: 900,
    glassTempMin: 70,
    glassTempMax: 80,
    maxBendingTemp: 100,
    coolingRate: 5,
    springBackFactor: 0.05,
    moistureSensitive: false,
    bendingCoefficient: 5,
    warningTemp: 110
  },
  {
    id: 'pvc_foam_5',
    name: 'PVC spienione 5mm',
    type: 'PVC_FOAM',
    densityKgM3: 550,
    thermalConductivity: 0.08,
    specificHeat: 900,
    glassTempMin: 70,
    glassTempMax: 80,
    maxBendingTemp: 100,
    coolingRate: 4,
    springBackFactor: 0.07,
    moistureSensitive: false,
    bendingCoefficient: 4,
    warningTemp: 110
  },
  {
    id: 'pvc_foam_10',
    name: 'PVC spienione 10mm',
    type: 'PVC_FOAM',
    densityKgM3: 550,
    thermalConductivity: 0.08,
    specificHeat: 900,
    glassTempMin: 75,
    glassTempMax: 85,
    maxBendingTemp: 105,
    coolingRate: 3,
    springBackFactor: 0.10,
    moistureSensitive: false,
    bendingCoefficient: 3,
    warningTemp: 115
  }
];

// Baza maszyn do gięcia
export const BENDING_MACHINES: BendingMachine[] = [
  {
    id: 'shannon_hrk_1200',
    name: 'Shannon HRK 1200',
    model: 'HRK',
    maxLength: 1200,
    tempRangeMin: 80,
    tempRangeMax: 200,
    heatingZones: 2,
    hasUpperHeater: true,
    hasLowerHeater: true,
    defaultHeatingWidth: 15
  },
  {
    id: 'shannon_hrk_2000',
    name: 'Shannon HRK 2000',
    model: 'HRK',
    maxLength: 2000,
    tempRangeMin: 80,
    tempRangeMax: 200,
    heatingZones: 3,
    hasUpperHeater: true,
    hasLowerHeater: true,
    defaultHeatingWidth: 20
  },
  {
    id: 'gt_2000',
    name: 'GT 2000',
    model: 'GT',
    maxLength: 2000,
    tempRangeMin: 70,
    tempRangeMax: 180,
    heatingZones: 2,
    hasUpperHeater: true,
    hasLowerHeater: true,
    defaultHeatingWidth: 18
  }
];

// Baza typowych defektów i ich przyczyn
export const BENDING_DEFECTS: BendingDefect[] = [
  {
    name: 'Białe smugi (stress whitening)',
    cause: 'Za wysoka temperatura lub za szybkie gięcie',
    solution: 'Zmniejsz temperaturę o 10-15°C, wydłuż czas nagrzewania',
    preventionTemp: { max: 160 }
  },
  {
    name: 'Pęknięcia na linii gięcia',
    cause: 'Za niska temperatura lub za szybkie gięcie',
    solution: 'Zwiększ temperaturę o 10-15°C, gięcie wykonuj wolniej',
    preventionTemp: { min: 110 }
  },
  {
    name: 'Pęcherze w materiale',
    cause: 'Wilgoć w materiale (szczególnie PMMA)',
    solution: 'Wysusz materiał przed gięciem (80°C przez 2-4h)',
    preventionTime: { min: 120 }
  },
  {
    name: 'Niedogięcie (spring-back)',
    cause: 'Niewystarczający czas chłodzenia lub za niska temperatura',
    solution: 'Wydłuż czas chłodzenia, zwiększ kompensację kąta',
    preventionTime: { min: 30 }
  },
  {
    name: 'Deformacja powierzchni',
    cause: 'Nierównomierne nagrzewanie',
    solution: 'Sprawdź równomierność grzałek, wyreguluj strefy grzania'
  },
  {
    name: 'Zmiana koloru',
    cause: 'Przekroczenie maksymalnej temperatury materiału',
    solution: 'Zmniejsz temperaturę, szczególnie dla kolorowych tworzyw',
    preventionTemp: { max: 170 }
  },
  {
    name: 'Fałdy na linii gięcia',
    cause: 'Za szeroka strefa nagrzewania',
    solution: 'Zmniejsz szerokość strefy grzania, użyj maskownic'
  },
  {
    name: 'Nierówna linia gięcia',
    cause: 'Nierównomierna temperatura wzdłuż linii',
    solution: 'Sprawdź wszystkie strefy grzania, wyczyść grzałki'
  }
];

// Funkcja pomocnicza do znajdowania materiału
export const findMaterial = (id: string): BendingMaterial | undefined => {
  return BENDING_MATERIALS.find(m => m.id === id);
};

// Funkcja pomocnicza do znajdowania maszyny
export const findMachine = (id: string): BendingMachine | undefined => {
  return BENDING_MACHINES.find(m => m.id === id);
};

// Funkcja do grupowania materiałów po typie
export const getMaterialsByType = (type: BendingMaterial['type']): BendingMaterial[] => {
  return BENDING_MATERIALS.filter(m => m.type === type);
};

// Funkcja do pobierania grubości dla danego typu materiału
export const getAvailableThicknesses = (type: BendingMaterial['type']): number[] => {
  const materials = getMaterialsByType(type);
  const thicknesses = materials.map(m => {
    const match = m.name.match(/(\d+)mm/);
    return match ? parseInt(match[1]) : 0;
  }).filter(t => t > 0);
  return [...new Set(thicknesses)].sort((a, b) => a - b);
};